import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../utils/api";
import { Webhook as WebhookIcon, Plus, Trash2, ArrowLeft, Play, Power, CheckCircle, XCircle } from "lucide-react";

const KIND_BADGES = {
  slack:   { label: "Slack",   color: "#4A154B" },
  teams:   { label: "Teams",   color: "#464EB8" },
  discord: { label: "Discord", color: "#5865F2" },
  generic: { label: "Generic", color: "#6B7280" },
};

const EVENT_LABELS = {
  run_complete_single: "Single-file run complete",
  run_complete_multi:  "Multi-file run complete",
  run_complete_chain:  "Chain run complete",
  schedule_failed:     "Schedule failed",
  anomaly_triggered:   "Anomaly triggered",
  chain_assigned:      "Chain assigned",
};

export default function WebhooksPage() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [kind, setKind] = useState("slack");
  const [events, setEvents] = useState([]);
  const [secret, setSecret] = useState("");
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const load = () => api.get("/webhooks").then(r => setRows(r.data || []));
  useEffect(() => { load(); }, []);

  const onCreate = async () => {
    setErr("");
    try {
      await api.post("/webhooks", { name: name.trim(), url: url.trim(), kind, events, secret: secret || null, active: true });
      setCreating(false); setName(""); setUrl(""); setEvents([]); setSecret("");
      load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
  };
  const onToggle = async (w) => { await api.put(`/webhooks/${w.id}`, { active: !w.active }); load(); };
  const onDelete = async (w) => { if (!window.confirm(`Delete webhook "${w.name}"?`)) return; await api.delete(`/webhooks/${w.id}`); load(); };
  const onTest   = async (w) => {
    try { await api.post(`/webhooks/${w.id}/test`); alert("Test sent — check the destination."); load(); }
    catch (e) { alert(`Failed: ${e.response?.data?.detail || e.message}`); load(); }
  };
  const toggleEvent = (e) => setEvents(events.includes(e) ? events.filter(x => x !== e) : [...events, e]);

  return (
    <Layout>
      <div className="animate-in" style={{ maxWidth: "1080px", margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><ArrowLeft size={18} /></button>
            <div>
              <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.5rem", margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <WebhookIcon size={18} style={{ color: "#4A154B" }} /> Webhooks
              </h1>
              <p style={{ color: "var(--text-muted)", fontSize: "0.82rem", margin: 0 }}>
                Fan out ReconX events to Slack, Teams, Discord, or any HTTP endpoint.
              </p>
            </div>
          </div>
          <button data-testid="webhook-add-btn" onClick={() => setCreating(!creating)} className="btn-primary"
            style={{ fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <Plus size={14} /> New webhook
          </button>
        </div>

        {creating && (
          <div data-testid="webhook-create-panel" className="recon-card" style={{ padding: "1.25rem", marginBottom: "1rem" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "0.75rem" }}>
              <div>
                <label style={{ fontSize: "0.72rem", fontWeight: 600, display: "block", marginBottom: "0.25rem" }}>Name</label>
                <input data-testid="webhook-name" value={name} onChange={e => setName(e.target.value)} placeholder="#ops channel" className="recon-input" style={{ width: "100%", fontSize: "0.9rem" }} />
              </div>
              <div>
                <label style={{ fontSize: "0.72rem", fontWeight: 600, display: "block", marginBottom: "0.25rem" }}>Kind</label>
                <select data-testid="webhook-kind" value={kind} onChange={e => setKind(e.target.value)} className="recon-input" style={{ width: "100%", fontSize: "0.9rem" }}>
                  {Object.entries(KIND_BADGES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginBottom: "0.75rem" }}>
              <label style={{ fontSize: "0.72rem", fontWeight: 600, display: "block", marginBottom: "0.25rem" }}>URL</label>
              <input data-testid="webhook-url" value={url} onChange={e => setUrl(e.target.value)}
                placeholder="https://hooks.slack.com/services/… or https://discord.com/api/webhooks/…" className="recon-input" style={{ width: "100%", fontSize: "0.85rem", fontFamily: "JetBrains Mono, monospace" }} />
            </div>
            <div style={{ marginBottom: "0.75rem" }}>
              <label style={{ fontSize: "0.72rem", fontWeight: 600, display: "block", marginBottom: "0.25rem" }}>Events to send</label>
              <div data-testid="webhook-events" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.35rem" }}>
                {Object.entries(EVENT_LABELS).map(([ek, label]) => (
                  <label key={ek} style={{ display: "flex", alignItems: "center", gap: "0.35rem", padding: "0.4rem 0.55rem", background: "#f9fafb", borderRadius: "0.35rem", fontSize: "0.82rem", cursor: "pointer" }}>
                    <input type="checkbox" data-testid={`webhook-event-${ek}`}
                      checked={events.includes(ek)} onChange={() => toggleEvent(ek)} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: "0.75rem" }}>
              <label style={{ fontSize: "0.72rem", fontWeight: 600, display: "block", marginBottom: "0.25rem" }}>Secret (optional — signs payload with HMAC-SHA256)</label>
              <input data-testid="webhook-secret" value={secret} onChange={e => setSecret(e.target.value)} placeholder="leave blank to skip signing" className="recon-input" style={{ width: "100%", fontSize: "0.85rem" }} />
            </div>
            {err && <div style={{ color: "#991b1b", fontSize: "0.82rem", marginBottom: "0.5rem" }}>{err}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.4rem" }}>
              <button onClick={() => setCreating(false)} className="btn-secondary" style={{ fontSize: "0.85rem" }}>Cancel</button>
              <button data-testid="webhook-save" onClick={onCreate} className="btn-primary" disabled={!name.trim() || !url.trim()}
                style={{ fontSize: "0.85rem", opacity: (name.trim() && url.trim()) ? 1 : 0.5 }}>
                Save webhook
              </button>
            </div>
          </div>
        )}

        <div className="recon-card" style={{ padding: "1rem 1.25rem" }}>
          {rows.length === 0 && !creating && (
            <div data-testid="webhooks-empty" style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)" }}>
              No webhooks yet. Click <b>New webhook</b> to wire up Slack, Teams, Discord, or any HTTP endpoint.
            </div>
          )}
          {rows.length > 0 && (
            <table data-testid="webhooks-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead>
                <tr>{["Name", "Kind", "Events", "Last delivery", "Status", "Actions"].map(h =>
                  <th key={h} style={{ textAlign: "left", padding: "0.5rem 0.75rem", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-muted)", fontWeight: 600, borderBottom: "1px solid #e5e7eb" }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map(w => (
                  <tr key={w.id} data-testid={`webhook-row-${w.id}`} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "0.6rem 0.75rem", fontWeight: 500 }}>{w.name}</td>
                    <td style={{ padding: "0.6rem 0.75rem" }}>
                      <span style={{ background: (KIND_BADGES[w.kind] || {}).color, color: "white", fontSize: "0.68rem", fontWeight: 700, padding: "0.15rem 0.5rem", borderRadius: "0.3rem" }}>
                        {(KIND_BADGES[w.kind] || {}).label || w.kind}
                      </span>
                    </td>
                    <td style={{ padding: "0.6rem 0.75rem", fontSize: "0.78rem", color: "var(--text-muted)" }}>{(w.events || []).length} event{(w.events || []).length === 1 ? "" : "s"}</td>
                    <td style={{ padding: "0.6rem 0.75rem", fontSize: "0.78rem", color: "var(--text-muted)" }}>
                      {w.last_at ? new Date(w.last_at).toLocaleString() : "—"}
                    </td>
                    <td style={{ padding: "0.6rem 0.75rem" }}>
                      {w.active
                        ? (w.last_status === "failed"
                            ? <span style={{ display: "inline-flex", alignItems: "center", gap: "0.2rem", padding: "0.15rem 0.55rem", borderRadius: "0.3rem", fontSize: "0.72rem", fontWeight: 600, background: "#fee2e2", color: "#991b1b" }}><XCircle size={10} /> Last delivery failed</span>
                            : <span style={{ display: "inline-flex", alignItems: "center", gap: "0.2rem", padding: "0.15rem 0.55rem", borderRadius: "0.3rem", fontSize: "0.72rem", fontWeight: 600, background: "#dcfce7", color: "#166534" }}><CheckCircle size={10} /> Active</span>)
                        : <span style={{ padding: "0.15rem 0.55rem", borderRadius: "0.3rem", fontSize: "0.72rem", fontWeight: 600, background: "#f3f4f6", color: "#6b7280" }}>Paused</span>}
                    </td>
                    <td style={{ padding: "0.6rem 0.75rem" }}>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button data-testid={`webhook-test-${w.id}`} onClick={() => onTest(w)} title="Send test payload"
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#16a34a" }}><Play size={14} /></button>
                        <button data-testid={`webhook-toggle-${w.id}`} onClick={() => onToggle(w)} title={w.active ? "Pause" : "Resume"}
                          style={{ background: "none", border: "none", cursor: "pointer", color: w.active ? "#a16207" : "#16a34a" }}><Power size={14} /></button>
                        <button data-testid={`webhook-del-${w.id}`} onClick={() => onDelete(w)} title="Delete"
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626" }}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
